import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FileSpreadsheet, 
  Video, 
  RefreshCw, 
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  X,
  Home,
  BookOpen
} from 'lucide-react';
import './EditCourseOptions.css';

const EditCourseOptions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
 
  const { courseId, courseData } = location.state || {};
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    
    if (!courseId) {
      console.error('No courseId found in state');
      navigate('/MHMV-profile/video-courses');
    }
  }, [courseId, navigate]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    
   
    setIsLoading(false);
  };

  const handleProceed = () => {
    if (!selectedOption || !courseId) return;
    
    setIsLoading(true);
    
    
    switch (selectedOption) {
      case 'excel':
        // Option 1: Update Only Excel
        navigate('/MHMV-profile/upload-course-excel', {
          state: {
            editMode: true,
            courseId: courseId,
            courseData: courseData,
            fromEditOptions: true,
            updateType: 'excelOnly',
            redirectTo: '/MHMV-profile/video-courses' 
          }
        });
        break;
        
      case 'videos':
        // Option 2: Update Only Videos
        navigate(`/MHMV-profile/upload-course-videos/${courseId}`, {
          state: {
            editMode: true,
            courseId: courseId,
            courseData: courseData,
            fromEditOptions: true,
            updateType: 'videosOnly',
            redirectTo: '/MHMV-profile/video-courses' 
          }
        });
        break;
        
      case 'both':
        // Option 3: Update Whole Course (Excel + Videos)
        navigate('/MHMV-profile/upload-course-excel', {
          state: {
            editMode: true,
            courseId: courseId,
            courseData: courseData,
            fromEditOptions: true,
            updateType: 'both',
            redirectTo: `/MHMV-profile/upload-course-videos/${courseId}` // Go to videos after Excel
          }
        });
        break;
        
      default:
        setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/MHMV-profile/video-courses');
  };

  const handleHome = () => {
    navigate('/MHMV-profile/video-course-home');
  };

  if (!courseId) {
    return (
      <div className="eco-container">
        <div className="eco-error-state">
          <X size={48} />
          <h2>Course Not Found</h2>
          <p>Unable to load course details. Please go back and try again.</p>
          <button onClick={handleBack} className="eco-primary-btn">
            <ArrowLeft size={18} />
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="eco-container">
      {/* Header */}
      <header className="eco-header">
        <div className="eco-header-content">
          <div className="eco-header-left">
            
            <div className="eco-header-titles">
              <h1>Update Course</h1>
              <p className="eco-subtitle">
                Select what you want to update for: <strong>{courseData?.title || 'This Course'}</strong>
              </p>
            </div>
          </div>
          <div className="eco-header-right">
            <button 
              className="eco-home-btn"
              onClick={handleHome}
            >
              <Home size={20} />
              Home
            </button>
          </div>
        </div>
      </header>

      <main className="eco-main-content">
       
        <div className="eco-options-grid">
          {/* Option 1: Update Only Excel */}
          <div 
            className={`eco-option-card ${selectedOption === 'excel' ? 'selected' : ''}`}
            onClick={() => handleOptionSelect('excel')}
          >
           
            <div className="eco-option-content">
              <h3>Update Only Excel</h3>
              <p className="eco-option-description">
                Update the course structure (modules, lessons, titles) without changing video files.
              </p>
              <div className="eco-flow-indicator">
                <div className="eco-flow-step">
                  <span className="eco-step-number">1</span>
                  <span className="eco-step-text">Update Excel File</span>
                </div>
                <ArrowRight size={16} className="eco-flow-arrow" />
                <div className="eco-flow-step">
                  <span className="eco-step-number">2</span>
                  <span className="eco-step-text">Back to Courses</span>
                </div>
              </div>
              <div className="eco-option-features">
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Videos remain unchanged</span>
                </div>
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Update course structure only</span>
                </div>
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Quick update process</span>
                </div>
              </div>
            </div>
            {selectedOption === 'excel' && (
              <div className="eco-selected-badge">
                Selected
              </div>
            )}
          </div>

          {/* Option 2: Update Only Videos */}
          <div 
            className={`eco-option-card ${selectedOption === 'videos' ? 'selected' : ''}`}
            onClick={() => handleOptionSelect('videos')}
          >
            
            <div className="eco-option-content">
              <h3>Update Only Videos</h3>
              <p className="eco-option-description">
                Upload new or replacement video files without changing the course structure.
              </p>
              <div className="eco-flow-indicator">
                <div className="eco-flow-step">
                  <span className="eco-step-number">1</span>
                  <span className="eco-step-text">Upload/Replace Videos</span>
                </div>
                <ArrowRight size={16} className="eco-flow-arrow" />
                <div className="eco-flow-step">
                  <span className="eco-step-number">2</span>
                  <span className="eco-step-text">Back to Courses</span>
                </div>
              </div>
              <div className="eco-option-features">
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Excel structure remains unchanged</span>
                </div>
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Replace specific videos</span>
                </div>
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Keep existing course layout</span>
                </div>
              </div>
            </div>
            {selectedOption === 'videos' && (
              <div className="eco-selected-badge">
                Selected
              </div>
            )}
          </div>

          {/* Option 3: Update Whole Course */}
          <div 
            className={`eco-option-card ${selectedOption === 'both' ? 'selected' : ''}`}
            onClick={() => handleOptionSelect('both')}
          >
            
            <div className="eco-option-content">
              <h3>Update Whole Course</h3>
              <p className="eco-option-description">
                Complete course update - change both structure (Excel) and video files.
              </p>
              <div className="eco-flow-indicator">
                <div className="eco-flow-step">
                  <span className="eco-step-number">1</span>
                  <span className="eco-step-text">Update Excel File</span>
                </div>
                <ArrowRight size={16} className="eco-flow-arrow" />
                <div className="eco-flow-step">
                  <span className="eco-step-number">2</span>
                  <span className="eco-step-text">Update Videos</span>
                </div>
                <ArrowRight size={16} className="eco-flow-arrow" />
                <div className="eco-flow-step">
                  <span className="eco-step-number">3</span>
                  <span className="eco-step-text">Back to Courses</span>
                </div>
              </div>
              <div className="eco-option-features">
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Full course revision</span>
                </div>
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Step-by-step process</span>
                </div>
                <div className="eco-feature">
                  <CheckCircle size={14} />
                  <span>Complete control</span>
                </div>
              </div>
            </div>
            {selectedOption === 'both' && (
              <div className="eco-selected-badge">
                Selected
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="eco-actions">
          <button 
            className="eco-cancel-btn"
            onClick={handleBack}
            disabled={isLoading}
          >
            <X size={18} />
            Cancel
          </button>
          
          <button 
            className="eco-proceed-btn"
            onClick={handleProceed}
            disabled={!selectedOption || isLoading}
          >
            {isLoading ? (
              <>
                <span className="eco-spinner"></span>
                Processing...
              </>
            ) : (
              <>
                Proceed to Update
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>

        {/* Help Text */}
        <div className="eco-help-text">
          <div className="eco-help-icon">
            <BookOpen size={16} />
          </div>
          <div className="eco-help-content">
            <p><strong>Note:</strong> Make sure your video filenames match the titles in Excel for proper mapping.</p>
            <p>Course ID: <code>{courseId}</code></p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EditCourseOptions;